import apiService from 'library/api-service';
import type { HolidayItem } from 'library/types';
import { useEffect, useMemo, useRef, useState } from 'react';

export type HolidayCacheStatus = 'idle' | 'loading' | 'ready' | 'error';

type HolidayCacheLoadState = {
  yearsKey: string;
  status: HolidayCacheStatus;
};

const normalizeYears = (years: readonly number[]): number[] =>
  Array.from(new Set(years.filter(Number.isInteger))).sort((left, right) => left - right);

/** Loads every requested year at most once during the page lifetime. */
export const useHolidayCache = (years: readonly number[]) => {
  const yearsKey = normalizeYears(years).join(',');
  const normalizedYears = useMemo(() => (yearsKey ? yearsKey.split(',').map(Number) : []), [yearsKey]);
  const cacheRef = useRef(new Map<number, HolidayItem[]>());
  const requestsRef = useRef(new Map<number, Promise<HolidayItem[]>>());
  const [cacheVersion, setCacheVersion] = useState(0);
  const [loadState, setLoadState] = useState<HolidayCacheLoadState>({ yearsKey: '', status: 'idle' });

  useEffect(() => {
    if (normalizedYears.length === 0) {
      setLoadState({ yearsKey, status: 'idle' });
      return;
    }

    if (normalizedYears.every((year) => cacheRef.current.has(year))) return;

    let active = true;
    setLoadState({ yearsKey, status: 'loading' });

    const requests = normalizedYears.map((year) => {
      const cachedItems = cacheRef.current.get(year);
      if (cachedItems) return Promise.resolve(cachedItems);

      const existingRequest = requestsRef.current.get(year);
      if (existingRequest) return existingRequest;

      const request = apiService.getHolidays(year).then((response) => {
        if (!response.isSuccess) throw new Error(response.message || `Could not load holidays for ${year}`);
        return response.data || [];
      });
      requestsRef.current.set(year, request);
      return request;
    });

    Promise.all(requests)
      .then((results) => {
        if (!active) return;

        normalizedYears.forEach((year, index) => {
          cacheRef.current.set(year, results[index]);
        });
        setCacheVersion((version) => version + 1);
        setLoadState({ yearsKey, status: 'ready' });
      })
      .catch(() => {
        if (active) setLoadState({ yearsKey, status: 'error' });
      });

    return () => {
      active = false;
    };
  }, [normalizedYears, yearsKey]);

  const holidays = useMemo(
    () => normalizedYears.flatMap((year) => cacheRef.current.get(year) || []),
    // cacheVersion is the explicit invalidation signal for the ref-backed cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheVersion, normalizedYears]
  );

  const hasEveryRequestedYear = normalizedYears.every((year) => cacheRef.current.has(year));
  const status: HolidayCacheStatus =
    normalizedYears.length === 0
      ? 'idle'
      : hasEveryRequestedYear
        ? 'ready'
        : loadState.yearsKey === yearsKey && loadState.status === 'error'
          ? 'error'
          : 'loading';

  return { holidays, status } as const;
};
